// src/utils/outline.js
//
// Derives the document outline (navigation tree) from the editor's structural
// heading blocks. A heading is any block carrying `data-heading-level` (1..4)
// and a stable `data-block-id` — stamped by the heading model in EditorPage.
//
// The outline is *derived*, never stored: the editor's contentEditable DOM is
// the single source of truth. A "chapter" is not a wrapper element but a range
// of blocks from one heading up to the next heading of the same-or-higher level.

const isTableWrap = (el) => !!el?.classList?.contains('rv-table-wrap');

/**
 * Is `el` the rest of `prev`, cut at a page break (splitBlocks.js)? The same
 * pairing joinSplitBlocks uses to put them back together: matching split id,
 * same tag, and both or neither a table.
 */
export function continuesBlock(el, prev) {
  const cont = el?.getAttribute?.('data-split-cont');
  return cont != null && !!prev
    && prev.getAttribute?.('data-split-id') === cont
    && prev.tagName === el.tagName
    && isTableWrap(prev) === isTableWrap(el);
}

/** Every page-split continuation in the editor, mapped to the block it continues. */
function splitContinuations(editorEl) {
  const conts = new Map();
  let prev = null;
  editorEl.querySelectorAll('.rv-page-body').forEach((body) => {
    Array.from(body.children).forEach((el) => {
      if (el.classList?.contains('rv-end-spacer')) return;
      if (continuesBlock(el, prev)) conts.set(el, prev);
      prev = el;
    });
  });
  return conts;
}

/**
 * Give every content block a unique `data-block-id`, assigning ids to blocks
 * that don't have one and re-assigning any that are blank or duplicated.
 *
 * Ids used to be stamped only when a block was styled as a heading, which left
 * ordinary paragraphs with no identity at all — so anything anchored to body
 * text (comments, most obviously) had nothing durable to point at. Every block
 * is addressable now; a block IS the unit you can anchor to.
 *
 * Duplicates arise naturally: contentEditable copies a block's attributes onto
 * the new paragraph when the user presses Enter, so one id can spread across
 * several blocks. First occurrence keeps the id, later ones are re-assigned.
 *
 * The trailing `.rv-end-spacer` is skipped — it's layout, not content.
 *
 * A block the page split cut in two (splitBlocks.js) is ONE block: its
 * continuation takes the id of the part before it (see continuesBlock). Treated as a duplicate it got a fresh id on every re-layout — a save
 * each time the outline was rebuilt, and a comment made on the second half
 * anchored to an id the next re-layout threw away.
 *
 * Mutates the live DOM; returns true if anything changed so the caller can
 * persist the correction.
 *
 * @param {Element|null} editorEl
 * @param {() => string} makeId fresh-id generator
 * @returns {boolean} whether any id was assigned or reassigned
 */
export function ensureBlockIds(editorEl, makeId) {
  if (!editorEl || typeof makeId !== 'function') return false;
  const seen = new Set();
  let changed = false;

  // Never hand out an id that already exists further down the document.
  const freshId = () => {
    let id = makeId();
    while (seen.has(id)) id = makeId();
    return id;
  };

  // The last block seen, across page bodies — a continuation's first part is
  // the final block of the page before.
  let prev = null;

  editorEl.querySelectorAll('.rv-page-body').forEach((body) => {
    Array.from(body.children).forEach((el) => {
      if (el.classList?.contains('rv-end-spacer')) return;
      const id = el.getAttribute?.('data-block-id');
      const prevId = prev?.getAttribute('data-block-id');
      if (prevId && continuesBlock(el, prev)) {
        if (id !== prevId) {
          el.setAttribute('data-block-id', prevId);
          changed = true;
        }
        // A block cut over three pages: the middle part pairs with the last.
        prev = el;
        return;
      }
      if (!id || seen.has(id)) {
        const fresh = freshId();
        el.setAttribute('data-block-id', fresh);
        seen.add(fresh);
        changed = true;
      } else {
        seen.add(id);
      }
      prev = el;
    });
  });
  return changed;
}

/**
 * Map every block to the chapter (heading block-id) that contains it.
 *
 * Derived from the live document rather than stored on each comment: a
 * paragraph can be moved into a different chapter, or its heading deleted,
 * and any value written at comment-time would then quietly disagree with the
 * document. Reading it fresh is self-healing.
 *
 * A heading maps to itself. Blocks before the first heading map to null —
 * they genuinely belong to no chapter.
 *
 * @param {Element|null} editorEl
 * @returns {Map<string, string|null>} block-id → chapter heading block-id
 */
export function buildBlockChapterMap(editorEl) {
  const map = new Map();
  if (!editorEl) return map;

  let currentChapter = null;
  editorEl.querySelectorAll('.rv-page-body').forEach((body) => {
    Array.from(body.children).forEach((el) => {
      if (el.classList?.contains('rv-end-spacer')) return;
      const id = el.getAttribute?.('data-block-id') || null;
      if (el.hasAttribute?.('data-heading-level')) currentChapter = id || currentChapter;
      if (id) map.set(id, currentChapter);
    });
  });
  return map;
}

/**
 * Read every heading block, in document order, across all pages.
 * @param {Element|null} editorEl the contentEditable host
 * @returns {Array<{blockId,level,text,pageNumber,el}>} flat list in doc order
 */
export function getHeadingBlocks(editorEl) {
  if (!editorEl) return [];
  const pages = Array.from(editorEl.querySelectorAll('.rv-page'));
  const out = [];
  // A chapter title cut at a page break is ONE heading: both parts carry the
  // marker and share an id, and listing both put the chapter in the outline
  // twice and shifted every later "Ch. N". The rest of the title joins the
  // first part's entry instead.
  const conts = splitContinuations(editorEl);
  const entryFor = new Map();
  pages.forEach((page, pageIdx) => {
    const body = page.querySelector('.rv-page-body');
    if (!body) return;
    body.querySelectorAll('[data-heading-level]').forEach((el) => {
      const level = Number(el.getAttribute('data-heading-level'));
      if (!(level >= 1 && level <= 4)) return;
      const text = (el.textContent || '').replace(/​/g, '').trim();
      if (conts.has(el)) {
        let head = conts.get(el);
        // Nearest earlier part with an entry: usually the first, but the middle
        // one when the cut left the first part empty.
        while (!entryFor.has(head) && conts.has(head)) head = conts.get(head);
        const entry = entryFor.get(head);
        if (entry) {
          if (text) entry.text = `${entry.text} ${text}`;
          return;
        }
        // The first part was left empty by the cut: this part is the heading.
      }
      // Skip empty headings — a blank line that inherited a heading marker is
      // not a real outline entry and would show as a phantom "Untitled" row.
      if (!text) return;
      const entry = {
        blockId: el.getAttribute('data-block-id') || null,
        level,
        text,
        pageNumber: pageIdx + 1,
        el,
      };
      entryFor.set(el, entry);
      out.push(entry);
    });
  });
  return out;
}

// Base-26 letters (a, b, ..., z, aa, ab, ...) — same scheme as spreadsheet
// column headers, for the depth-1 "a." tier of a Google Docs-style outline.
function toAlpha(n) {
  let s = '';
  while (n > 0) {
    n -= 1;
    s = String.fromCharCode(97 + (n % 26)) + s;
    n = Math.floor(n / 26);
  }
  return s;
}

const ROMAN_TABLE = [
  [1000, 'm'], [900, 'cm'], [500, 'd'], [400, 'cd'], [100, 'c'], [90, 'xc'],
  [50, 'l'], [40, 'xl'], [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i'],
];
function toRoman(n) {
  let s = '';
  ROMAN_TABLE.forEach(([value, symbol]) => {
    while (n >= value) { s += symbol; n -= value; }
  });
  return s;
}

// Google Docs' default multilevel-list cycle: decimal, alpha, roman, then
// repeats — so nesting past level 3 never renders worse than lower-roman.
function formatOrdinal(n, depth) {
  const style = depth % 3;
  if (style === 1) return toAlpha(n);
  if (style === 2) return toRoman(n);
  return String(n);
}

// Assign each node a display label ("1", "a", "i", ...) based on its 1-based
// position among same-parent siblings, keyed off tree depth (not heading
// level) so a demoted chapter always gets its own visible ordering.
function labelTree(nodes, depth) {
  nodes.forEach((node, i) => {
    node.label = formatOrdinal(i + 1, depth);
    if (node.children.length) labelTree(node.children, depth + 1);
  });
}

/**
 * Build the outline as both a flat list and a nested tree.
 * Each tree node also carries `label` — its position among same-parent
 * siblings, formatted per Google Docs' decimal/alpha/roman depth cycle, so
 * the panel can render an ordinal at every nesting level, not just level 1.
 * @param {Element|null} editorEl
 * @returns {{ flat: Array, tree: Array }}
 */
export function buildOutline(editorEl) {
  const flat = getHeadingBlocks(editorEl);
  const enriched = flat.map((h) => ({ ...h, children: [] }));

  // Nest by ascending level using a stack of open ancestors.
  const tree = [];
  const stack = [];
  enriched.forEach((node) => {
    while (stack.length && stack[stack.length - 1].level >= node.level) {
      stack.pop();
    }
    if (stack.length) stack[stack.length - 1].children.push(node);
    else tree.push(node);
    stack.push(node);
  });

  labelTree(tree, 0);

  return { flat: enriched, tree };
}

/**
 * Return the ordered block elements that make up a chapter/section: the heading
 * with `blockId`, plus every following block up to (not including) the next
 * heading of the same-or-higher level. Blocks are gathered across page bodies
 * in document order. The trailing `.rv-end-spacer` of each body is skipped.
 * @param {Element|null} editorEl
 * @param {string} blockId
 * @returns {Element[]} the chapter's blocks (empty if not found)
 */
/**
 * Given any block (e.g. the one holding the caret), return the block-id of the
 * heading whose chapter it belongs to — the nearest heading at or above it in
 * document order. Used to reorder "the chapter I'm editing" via the keyboard.
 * @param {Element|null} editorEl
 * @param {Element|null} fromEl a block inside a page body
 * @returns {string|null}
 */
export function getChapterHeadingId(editorEl, fromEl) {
  if (!editorEl || !fromEl) return null;
  const block = fromEl.closest?.('.rv-page-body > *') || fromEl;
  const blocks = [];
  editorEl.querySelectorAll('.rv-page-body').forEach((body) => {
    Array.from(body.children).forEach((child) => {
      if (!child.classList?.contains('rv-end-spacer')) blocks.push(child);
    });
  });
  let idx = blocks.indexOf(block);
  if (idx === -1) return null;
  for (let i = idx; i >= 0; i--) {
    if (blocks[i].hasAttribute?.('data-heading-level')) {
      return blocks[i].getAttribute('data-block-id');
    }
  }
  return null;
}

/**
 * Return the block-id of a heading's same-level, same-parent sibling in the
 * given direction ('up' | 'down'), or null if it's already at that edge. A
 * heading of a *higher* level (smaller number) marks a parent boundary and
 * stops the search; deeper headings are skipped as descendants.
 * @param {Element|null} editorEl
 * @param {string} blockId
 * @param {'up'|'down'} direction
 * @returns {string|null}
 */
export function getSiblingChapterId(editorEl, blockId, direction) {
  const flat = getHeadingBlocks(editorEl);
  const i = flat.findIndex((h) => h.blockId === blockId);
  if (i === -1) return null;
  const level = flat[i].level;
  const step = direction === 'up' ? -1 : 1;
  for (let j = i + step; j >= 0 && j < flat.length; j += step) {
    if (flat[j].level < level) return null;      // hit parent boundary
    if (flat[j].level === level) return flat[j].blockId; // sibling
    // deeper level → descendant, keep scanning
  }
  return null;
}

export function getChapterRange(editorEl, blockId) {
  if (!editorEl || !blockId) return [];

  // Flatten all content blocks (across pages) in document order.
  const blocks = [];
  editorEl.querySelectorAll('.rv-page-body').forEach((body) => {
    Array.from(body.children).forEach((child) => {
      if (!child.classList?.contains('rv-end-spacer')) blocks.push(child);
    });
  });

  const startIdx = blocks.findIndex(
    (b) => b.getAttribute?.('data-block-id') === blockId
  );
  if (startIdx === -1) return [];

  const startLevel = Number(blocks[startIdx].getAttribute('data-heading-level')) || 1;
  const range = [blocks[startIdx]];

  for (let i = startIdx + 1; i < blocks.length; i++) {
    const b = blocks[i];
    const lvl = Number(b.getAttribute?.('data-heading-level'));
    // Stop at the next heading of same-or-higher rank (lvl <= startLevel) —
    // but not at the rest of a heading cut at a page break, which is the same
    // heading (moving the chapter must take both parts).
    if (lvl >= 1 && lvl <= startLevel && !continuesBlock(b, blocks[i - 1])) break;
    range.push(b);
  }
  return range;
}

/**
 * Decide which heading the CHAPTERS panel should highlight, from geometry alone.
 *
 * Scroll-spy rule: the active heading is the last one whose top has scrolled
 * above a marker line `markerOffset` below the top of the scroll container —
 * i.e. the heading you are currently reading under. Before the first heading
 * reaches the marker, the first heading stays selected.
 *
 * `pinnedId` overrides that. A click in the panel scrolls the chosen heading to
 * the *centre* of the viewport, which leaves it far below the marker line, so
 * pure scroll-spy would resolve to the heading above it — the chapter the
 * author did not click. While a jump is pinned, the pinned heading wins.
 *
 * Pure: takes measurements, returns a block id. No DOM, no state.
 *
 * @param {Array<{blockId: string|null, top: number}>} heads headings in document
 *   order, `top` being the viewport-relative top edge of each heading block
 * @param {object} opts
 * @param {number} opts.containerTop viewport-relative top edge of the scroller
 * @param {number} [opts.markerOffset=120] distance below `containerTop` of the
 *   line a heading must cross to become the one being read
 * @param {string|null} [opts.pinnedId] heading pinned by an explicit jump; used
 *   only when it is still present in `heads`
 * @returns {string|null} block id to highlight, or null when there are none
 */
export function resolveActiveHeadingId(heads, { containerTop = 0, markerOffset = 120, pinnedId = null } = {}) {
  if (!Array.isArray(heads) || heads.length === 0) return null;

  // A pinned chapter can vanish between the click and the next scroll (deleted,
  // or its heading demoted away) — fall through to geometry when it has.
  if (pinnedId && heads.some((h) => h.blockId === pinnedId)) return pinnedId;

  let current = heads[0];
  for (const h of heads) {
    if (h.top - containerTop <= markerOffset) current = h;
    else break;
  }
  return current.blockId ?? null;
}
